import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PricingCards from "./components/PricingCards";
import FeatureTable from "./components/FeatureTable";

const TRUST_ITEMS = [
  { icon: "ri-shield-check-line", label: "Dövlət Standartları", color: "text-blue-600", bg: "bg-blue-50" },
  { icon: "ri-id-card-line", label: "FİN Doğrulaması", color: "text-green-600", bg: "bg-green-50" },
  { icon: "ri-lock-2-line", label: "AES-256 Şifrələmə", color: "text-purple-600", bg: "bg-purple-50" },
  { icon: "ri-customer-service-2-line", label: "24/7 Dəstək", color: "text-red-600", bg: "bg-red-50" },
];

export default function Pricing() {
  const { t } = useTranslation();
  const [annual, setAnnual] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-6 bg-gradient-to-b from-stone-50 to-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <span className="inline-block bg-red-50 text-red-600 text-xs font-bold px-4 py-1.5 rounded-full tracking-widest uppercase mb-4">
            {t("pricing.hero_tag")}
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
            {t("pricing.hero_title")}
          </h1>
          <p className="text-gray-500 text-base max-w-xl mx-auto mb-10 leading-relaxed">
            {t("pricing.hero_subtitle")}
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-stone-100 rounded-full p-1 gap-1 mb-14">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                !annual ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {t("pricing.monthly")}
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all whitespace-nowrap flex items-center gap-2 ${
                annual ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {t("pricing.annual")}
              <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                {t("pricing.annual_badge")}
              </span>
            </button>
          </div>

          {/* Cards */}
          <PricingCards annual={annual} />
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-12 bg-white border-y border-stone-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {TRUST_ITEMS.map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-3 text-center">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${item.bg}`}>
                  <i className={`${item.icon} text-lg ${item.color}`} />
                </div>
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <FeatureTable />

      {/* FAQ mini */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-extrabold text-gray-900">Tez-tez Verilən Suallar</h2>
          </div>
          <div className="space-y-3">
            {[
              {
                q: "Pulsuz plandan ödənişli plana keçid necə olur?",
                a: "İstənilən vaxt hesab parametrlərinizindən plan dəyişdirə bilərsiniz. Ödəniş dərhal emal edilir, yeni imkanlar anında aktivləşir.",
              },
              {
                q: "İllik plan üçün ödəniş necə aparılır?",
                a: "İllik planlarda ödəniş bir dəfəyə aparılır. Bu zaman 2 aylıq endirim avtomatik tətbiq olunur.",
              },
              {
                q: "Korporativ plan üçün xüsusi tələblər var?",
                a: "Bəli. Korporativ müştərilər üçün xüsusi SLA müqaviləsi, prioritet onboarding və fərdi konfiqurasiya mövcuddur. Satış komandamızla əlaqə saxlayın.",
              },
              {
                q: "Ödəniş üsulları hansılardır?",
                a: "Azərbaycan bank kartları (Visa, Mastercard), Kapital Bank, PAŞA Bank ödəniş şlüzləri dəstəklənir.",
              },
            ].map((item, i) => (
              <FAQItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="relative rounded-3xl overflow-hidden bg-gray-900 px-8 md:px-16 py-14 text-center">
            <div className="absolute inset-0 opacity-10">
              <img
                src="https://readdy.ai/api/search-image?query=dark%20abstract%20geometric%20pattern%20with%20glowing%20lines%20and%20nodes%2C%20deep%20space%20technology%20backdrop%2C%20minimal%20dark%20blue%20navy%20background%2C%20futuristic%20network%20design&width=1200&height=400&seq=pricing_cta&orientation=landscape"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
                {t("pricing.cta_title")}
              </h2>
              <p className="text-white/60 text-sm mb-8 max-w-md mx-auto leading-relaxed">
                {t("pricing.cta_subtitle")}
              </p>
              <Link
                to="/support"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3.5 rounded-full cursor-pointer transition-all text-sm whitespace-nowrap"
              >
                <i className="ri-message-3-line" />
                {t("pricing.cta_btn")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-stone-50 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 cursor-pointer"
      >
        <span className="font-medium text-gray-900 text-sm">{q}</span>
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
          <i className={`text-gray-400 transition-transform duration-200 ${open ? "ri-subtract-line" : "ri-add-line"}`} />
        </div>
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-sm text-gray-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}
